import { CanvasElement, StepElement } from '../canvas-element';
import ConnectorLine, { ConnectorLineConfig } from '../connector-line/ConnectorLine';
import { BaseFlowConfig, CanvasElementConfig, FlowConnector, FlowItem, StepItem } from '../TypeDefs';

/**
 * @method getCanvasElements
 * @description Retrieves the canvas elements based on the provided configuration and data.
 * @param {Object} options - The options for retrieving the canvas elements.
 * @param {CanvasElement[]} options.canvasElements - The array of existing canvas elements.
 * @param {FlowItem[]} options.items - The array of flow items.
 * @param {StepItem[]} options.stepItems - The array of step items.
 * @param {FlowConnector[]} options.connectors - The array of flow connectors.
 * @param {BaseFlowConfig} options.newConfig - The new configuration for the canvas elements.
 * @param {string} [options.selectedItemNodeId] - The ID of the selected item node.
 * @param {Object} [options.selectedConnector] - The selected connector object.
 * @param {string} options.selectedConnector.to - The ID of the "to" element of the selected connector.
 * @param {string} options.selectedConnector.from - The ID of the "from" element of the selected connector.
 * @returns {Object} An object containing the new canvas elements.
 * @property {CanvasElement[]} newElements - The new canvas elements.
 */
export const getCanvasElements = ({
  canvasElements,
  items,
  stepItems,
  connectors,
  newConfig,
  selectedItemNodeId,
  selectedConnector,
}: {
  canvasElements: CanvasElement[],
  items: FlowItem[],
  stepItems: StepItem[],
  connectors: FlowConnector[],
  newConfig: BaseFlowConfig,
  selectedItemNodeId?: string,
  selectedConnector?: { to: string; from: string; },
}) => {
  const canvasElementConfig = generateCanvasElementConfig(newConfig);
  const connectorLineConfig = generateConnectorLineConfig(newConfig);
  const newElements: CanvasElement[] = [];
  // for faster accessing the existing elements
  const elementsMap: { [key: string]: CanvasElement } = {};
  canvasElements.forEach((e) => {
    // clear all connecters, those will be recreated
    e.clearConnectors();
    elementsMap[e.elementId] = e;
  });
  items.forEach((flowItem) => {
    const prevElement = elementsMap[flowItem.nodeId];
    const elem = getCanvasElementFromFlowItem({
      flowItem,
      prevElement,
      canvasElementConfig,
      selectedItemNodeId,
      isStackStyled: flowItem.isStackedStyled,
    });
    newElements.push(elem);
    elementsMap[elem.elementId] = elem;
  });
  const stepElementConfig = generateCanvasElementConfig(newConfig, true);
  stepItems.forEach((stepItem) => {
    const prevElement = elementsMap[stepItem.nodeId];
    const elem = getStepElementFromStepItem({
      stepItem,
      prevElement,
      canvasElementConfig: stepElementConfig,
      selectedItemNodeId,
      isStackStyled: stepItem.isStackedStyled,
    });
    newElements.push(elem);
    elementsMap[elem.elementId] = elem;
  });
  connectors.forEach((connector) => {
    const to = elementsMap[connector.toNodeId];
    if (to) {
      const from = elementsMap[connector.fromNodeId];
      if (stepItems.length !== 0 && !from) {
        return;
      }
      const line = ConnectorLine.create({
        fromElement: from,
        toElement: to,
        orientation: connector.orientation,
        isSelected: !!from && !!selectedConnector
          && from.elementId === selectedConnector.from
          && to.elementId === selectedConnector.to,
        selectable: !!from,
        text: connector.text,
        tooltipText: connector.tooltip,
        buttons: connector.buttons,
        config: { ...connectorLineConfig, ...connector.config },
        displayEventVersion: connector.displayEventVersion ? `Ver ${connector.displayEventVersion}` : undefined,
      });
      from?.addOutgoingConnector(line);
      to.addIncomingConnector(line);
    }
  });

  return { newElements };
};

/**
 * @method getCanvasElementFromFlowItem
 * @@description Get the CanvasElement from the FlowItem.
 * @param {object} params - The parameters for getting the CanvasElement.
 * @param {FlowItem} params.flowItem - The FlowItem object.
 * @param {CanvasElement} params.prevElement - The previous CanvasElement.
 * @param {CanvasElementConfig} params.canvasElementConfig - The configuration for the CanvasElement.
 * @param {string} params.selectedItemNodeId - The ID of the selected item.
 * @param {boolean} [params.isStackStyled=false] - Indicates if the element is stack styled.
 * @returns {CanvasElement} The CanvasElement object.
 */
export const getCanvasElementFromFlowItem = (
  {
    flowItem,
    prevElement,
    canvasElementConfig,
    selectedItemNodeId,
    isStackStyled = false,
  }: {
    flowItem: FlowItem;
    prevElement: CanvasElement | undefined;
    canvasElementConfig: CanvasElementConfig;
    selectedItemNodeId: string | undefined;
    isStackStyled?: boolean;
  }
) => {
  if (
    prevElement
    && (prevElement.rev === flowItem.revision)
  ) {
    // element already exists, no need to create pass to new state
    prevElement.name = flowItem.name;
    prevElement.description = `${flowItem.subTitle} \n ${flowItem.description}`.trim();
    prevElement.isSelected = prevElement.elementId === selectedItemNodeId;
    prevElement.config = { ...canvasElementConfig, ...flowItem.config };
    prevElement.conditionText = flowItem.conditionText;
    return prevElement;
  }
  // completely new element or existing element with different rev
  return CanvasElement.create({
    elementId: flowItem.nodeId,
    rev: flowItem.revision,
    left: flowItem.x,
    top: flowItem.y,
    name: flowItem.name,
    description: `${flowItem.subTitle} \n ${flowItem.description}`.trim(),
    displayVersion: flowItem.displayVersion ? `Ver ${flowItem.displayVersion}` : undefined,
    buttons: flowItem.buttons,
    isSelected: selectedItemNodeId === flowItem.nodeId,
    icon: flowItem.icon,
    selectedIcon: flowItem.selectedIcon,
    isRoot: flowItem.isRoot,
    isError: flowItem.isError,
    isStackStyled,
    conditionText: flowItem.conditionText,
    config: { ...canvasElementConfig, ...flowItem.config },
    incomingIcon: flowItem.incomingIcon,
    outgoingIcon: flowItem.outgoingIcon,
    payloadTransformSelected: flowItem.isPayloadTransformationDataSelected
  });
};

/**
 * @method getStepElementFromStepItem
 * @description Get the StepElement from the StepItem.
 * @param {object} params - The parameters for getting the StepElement.
 * @param {StepItem} params.stepItem - The StepItem object.
 * @param {CanvasElement} params.prevElement - The previous CanvasElement.
 * @param {CanvasElementConfig} params.canvasElementConfig - The configuration for the CanvasElement.
 * @param {string} params.selectedItemNodeId - The ID of the selected item.
 * @param {boolean} [params.isStackStyled=false] - Indicates if the element is stack styled.
 * @returns {StepElement} The StepElement object.
 */
export const getStepElementFromStepItem = (
  {
    stepItem,
    prevElement,
    canvasElementConfig,
    selectedItemNodeId,
    isStackStyled = false,
  }: {
    stepItem: StepItem;
    prevElement: CanvasElement | undefined;
    canvasElementConfig: CanvasElementConfig;
    selectedItemNodeId: string | undefined;
    isStackStyled?: boolean;
  }
) => {
  if (
    prevElement
    && (prevElement.rev === stepItem.revision)
  ) {
    // element already exists, no need to create pass to new state
    prevElement.name = stepItem.name;
    prevElement.isSelected = prevElement.elementId === selectedItemNodeId;
    prevElement.config = { ...canvasElementConfig, ...stepItem.config };
    return prevElement;
  }
  // completely new element or existing element with different rev
  return StepElement.createStep({
    elementId: stepItem.nodeId,
    rev: stepItem.revision,
    left: stepItem.x,
    top: stepItem.y,
    name: stepItem.name,
    buttons: stepItem.buttons,
    isSelected: selectedItemNodeId === stepItem.nodeId,
    stepIcon: stepItem.stepIcon,
    isRoot: stepItem.isRoot,
    isStackStyled,
    config: { ...canvasElementConfig, ...stepItem.config },
    incomingIcon: stepItem.incomingIcon,
    outgoingIcon: stepItem.outgoingIcon,
    serviceCount: stepItem.serviceCount,
    eventCount: stepItem.eventCount,
    cemCount: stepItem.cemCount,
    serviceCountIcon: stepItem.serviceCountIcon,
    eventCountIcon: stepItem.eventCountIcon,
    cemCountIcon: stepItem.cemCountIcon,
    menuDots: stepItem.menuDotsIcon,
    stepConnectorIcon: stepItem.stepConnectorIcon
  });
};

/**
 * @method generateCanvasElementConfig
 * @description Generate the CanvasElementConfig based on the provided configuration.
 * @param {BaseFlowConfig} config - The base flow configuration.
 * @param {boolean} [stepCard=false] - Indicates if the configuration is for a step card.
 * @returns {CanvasElementConfig} The generated CanvasElementConfig.
 */
export const generateCanvasElementConfig = (
  config: BaseFlowConfig,
  stepCard = false,
): CanvasElementConfig => ({
  strokeWidth: config.itemStrokeWidth,
  inConnectorRadius: config.itemConnectorInRadius,
  outConnectorRadius: config.itemConnectorOutRadius,
  fillColor: config.itemFillColor,
  errorColor: config.itemErrorColor,
  selectedFillColor: config.itemSelectedFillColor,
  strokeColor: config.itemStrokeColor,
  selectedStrokeColor: config.itemSelectedStrokeColor,
  incomingFillColor: config.itemInConnectorColor,
  selectedIncomingFillColor: config.itemSelectedInConnectorColor,
  outgoingFillColor: config.itemOutConnectorColor,
  selectedOutgoingFillColor: config.itemSelectedOutConnectorColor,
  nameColor: config.itemNameColor,
  selectedNameColor: config.itemSelectedNameColor,
  descriptionColor: config.itemDescriptionColor,
  selectedDescriptionColor: config.itemSelectedDescriptionColor,
  fontFamily: config.fontFamily,
  nameFontSize: config.itemNameFontSize,
  versionFontSize: config.itemVersionFontSize,
  descriptionFontSize: config.itemDescriptionFontSize,
  orientation: config.orientation,
  lineHeightRatio: config.lineHeightRatio,
  padding: config.itemPadding,
  canvasBackgroundColor: config.canvasBackgroundColor,
  connectorOutlined: config.itemConnectorOutlined,
  draggable: config.draggable,
  btnGroupBackgroundColor: config.itemBtnGroupBackgroundColor,
  btnGroupBorderRadius: config.itemBtnGroupRadius,
  conditionTextFontSize: config.itemConditionTextFontSize,
  conditionTextConnectorStrokeWidth: config.connectorStrokeWidth,
  conditionTextConnectorStrokeColor: config.connectorStrokeColor,
  conditionTextBgColor: config.itemConditionTextBgColor,
  conditionTextFgColor: config.itemConditionTextFgColor,
  width: stepCard ? config.stepCardWidth : config.itemWidth,
  height: stepCard ? config.stepCardHeight : config.itemHeight,
});

/**
 * @method generateConnectorLineConfig
 * @description Generate the ConnectorLineConfig based on the provided configuration.
 * @param {BaseFlowConfig} config - The base flow configuration.
 * @returns {ConnectorLineConfig} The generated ConnectorLineConfig.
 */
export const generateConnectorLineConfig = (
  config: BaseFlowConfig,
): ConnectorLineConfig => ({
  strokeWidth: config.connectorStrokeWidth,
  strokeColor: config.connectorStrokeColor,
  selectedStrokeColor: config.connectorSelectedStrokeColor,
  fillColor: config.connectorArrowColor,
  selectedFillColor: config.connectorSelectedFillColor,
  clickRadius: config.connectorClickRadius,
  startConnectorStrokeColor: config.startBtnSelectionColor,
  startBtnHeight: config.startBtnHeight,
  startBtnWidth: config.startBtnWidth,
  startBtnConnectorRadius: config.itemConnectorOutRadius,
  fontFamily: config.fontFamily,
  textFontSize: config.connectorTextFontSize,
  lineHeightRatio: config.lineHeightRatio,
  textColor: config.connectorFillColor,
  clickShape: 'circle',
  strokeStyle: 'solid'
});
